"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  type PanInfo,
  motion,
  AnimatePresence,
  useDragControls,
} from "framer-motion";
import { X } from "lucide-react";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IBottomSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
  /** `tall`: a fixed 90% of the screen, so the sheet keeps its size as its content changes. */
  height?: "auto" | "full" | "half" | "tall";
}

const BottomSheetModal: React.FC<IBottomSheetModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  showBackButton = false,
  onBack,
  height = "auto",
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  // Dragged by its handle and header only. A drag listener on the whole sheet gave
  // every field inside `user-select: none` (a broken caret on iOS) and took touches
  // meant for the content.
  const dragControls = useDragControls();
  const startDrag = (event: React.PointerEvent) => {
    if ((event.target as HTMLElement).closest("button")) return;
    dragControls.start(event);
  };

  const getInitialY = () => {
    if (height === "full") return 0; // go to top for full-height sheet
    if (height === "half") return 50;
    return 0;
  };
  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    const shouldClose =
      info.velocity.y > 20 || (info.velocity.y >= 0 && info.point.y > 45);
    if (shouldClose) {
      onClose();
    }
  };

  useLockBodyScroll(isOpen);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  // Portalled above page chrome such as the chat composer (z-[999999]).
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[1000000]"
            onClick={onClose}
          />

          {/* Bottom Sheet */}
          <motion.div
            ref={sheetRef}
            initial={{ y: "100%" }}
            animate={{ y: getInitialY() }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300,
            }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            className={`
                            fixed bottom-0 left-0 right-0 z-[1000000] bg-white rounded-t-2xl shadow-2xl
                            ${height === "full" ? "h-full" : height === "half" ? "h-1/2" : height === "tall" ? "h-[90vh]" : "max-h-[90vh]"}
                            overflow-hidden flex flex-col
                        `}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag area: the handle and the header */}
            <div onPointerDown={startDrag} className="touch-none">
              {/* Drag Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>

              {/* ChatHeading */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                {showBackButton ? (
                  <button
                    onClick={onBack}
                    aria-label="Back"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                  </button>
                ) : (
                  <div className="w-9" />
                )}

                <div className="flex-1 text-center">
                  <h2 className="text-lg font-semibold text-ink-black">
                    {title}
                  </h2>
                  {subtitle && (
                    <p className="mt-0.5 text-sm text-ink-secondary">
                      {subtitle}
                    </p>
                  )}
                </div>

                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} className="text-ink-secondary" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default BottomSheetModal;
